import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { post } from "api/api-methods";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useError } from "hooks/use-error";
import { trackEvent } from "logger/logger";
import { pageTrackingService, TRACKING_VERSION } from "logger/page-tracking";
import { useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { State } from "store/store-types";
import { CreateReservationParameters } from "types/request-parameters";
import { WebBooking } from "types/web-booking";
import { isGuid } from "utils";

export const useCreateBooking = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const cancelBooking = useCancelBooking();
    const { isActivityBooking } = useBookingStateContext();

    const createReservation = async ({
        state,
        isInquiry,
    }: {
        state: State;
        isInquiry?: boolean;
    }) => {
        const data: CreateReservationParameters = new CreateReservationParameters(
            state,
            undefined,
            isInquiry
        );
        let booking: WebBooking;
        const startTime = new Date().getTime();

        try {
            booking = await post<WebBooking, CreateReservationParameters>(
                `WebBooking/WebBookings`,
                data
            );
            const pathParts = window.location.pathname.split("/").filter((part) => part);

            // Check if the last segment is a GUID
            const lastPart = pathParts[pathParts.length - 1] as Routes;
            const hasGuidAtEnd = isGuid(lastPart);

            // Determine if we should update the route with the booking GUID
            const shouldUpdateRouteWithGuid = (() => {
                // Always update if there's already a GUID at the end
                if (hasGuidAtEnd) return true;

                // For simple bookings, update when on finalize or preorder route, or on experience view
                if (
                    !isActivityBooking &&
                    (lastPart === Routes.FINALIZE ||
                        lastPart === Routes.PREORDER ||
                        lastPart === Routes.EXPERIENCE_VIEW)
                )
                    return true;

                // For activity bookings, update when on availability route, or on experience view
                if (
                    isActivityBooking &&
                    (lastPart === Routes.AVAILABILITY || lastPart === Routes.EXPERIENCE_VIEW)
                )
                    return true;

                return false;
            })();

            if (shouldUpdateRouteWithGuid) {
                // Replace or append the GUID
                if (hasGuidAtEnd) {
                    pathParts[pathParts.length - 1] = booking.guid;
                } else {
                    pathParts.push(booking.guid);
                }

                // Reconstruct the path
                const pathname = `/${pathParts.join("/")}`;

                void navigate(
                    {
                        pathname,
                        search: location.search,
                    },
                    { replace: true }
                );

                // update the React Query cache
                queryClient.setQueryData<WebBooking>(
                    ["booking", booking.guid],
                    new WebBooking(booking)
                );

                const endTime = new Date().getTime();
                const loadTime = endTime - startTime;
                const pageContext = pageTrackingService.getCurrentContext();

                // Track booking creation success
                trackEvent("Booking.Created.Success", {
                    guests: state.guests ?? 0,
                    children: state.guestsChildren ?? 0,
                    date: state.date?.toISOString() ?? "",
                    isInquiry: isInquiry ?? false,
                    sessionId: pageContext.sessionId,
                    durationMs: loadTime,
                    trackingVersion: TRACKING_VERSION,
                });

                return booking;
            } else {
                cancelBooking.mutate({ id: booking.guid, systemTriggered: true });
                return booking;
            }
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            const errorCode = response?.code;

            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            const pageContext = pageTrackingService.getCurrentContext();

            // Track booking creation failure
            trackEvent("Booking.Created.Failure", {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.date?.toISOString() ?? "",
                isInquiry: isInquiry ?? false,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                errorMessage: error instanceof Error ? error.message : "Unknown error",
                trackingVersion: TRACKING_VERSION,
            });

            throw error;
        }
    };

    return useMutation({
        mutationKey: ["create-booking"],
        mutationFn: createReservation,
        scope: {
            id: "webbooking-create",
        },
    });
};
